/**
 * Consolidated WebSocket System - Main Entry Point
 * Single WebSocket client per session with service registry and message routing
 */

import { AppDispatch } from '@/store/index';
import { ConsolidatedWebSocketManager, consolidatedWebSocketManager } from './consolidatedManager';
import { DeviceWebSocketService, deviceWebSocketService } from './deviceService';
import { ValveNodeWebSocketService, valveNodeWebSocketService } from './valveNodeService';
import { AlertWebSocketService, alertWebSocketService } from './alertService';
import { ProgramExecutionWebSocketService, programExecutionWebSocketService } from './programExecutionService';
import { optaWebSocketService } from './optaService';
import { WebSocketOptions } from './types';

// Feature flags (consolidated system is now default)
const WEBSOCKET_FEATURE_FLAGS = {
  USE_CONSOLIDATED_MANAGER: import.meta.env.REACT_APP_USE_CONSOLIDATED_WEBSOCKET !== 'false', // Default to true
  LOG_MIGRATION_EVENTS: import.meta.env.REACT_APP_LOG_WS_MIGRATION === 'true'
};

console.log('🔧 WebSocket System - Using consolidated system');

// Main WebSocket System class
export class WebSocketSystem {
  private static instance: WebSocketSystem;
  private consolidatedManager: ConsolidatedWebSocketManager;
  private initialized = false;
  
  private constructor() {
    this.consolidatedManager = consolidatedWebSocketManager;
  }
  
  public static getInstance(): WebSocketSystem {
    if (!WebSocketSystem.instance) {
      WebSocketSystem.instance = new WebSocketSystem();
    }
    return WebSocketSystem.instance;
  }
  
  // Initialize the WebSocket system
  async initialize(dispatch: AppDispatch, options?: WebSocketOptions): Promise<void> {
    if (this.initialized) {
      console.log('⚠️ WebSocket System - Already initialized');
      return;
    }
    
    console.log('🚀 WebSocket System - Initializing consolidated system...');
    
    // Register default services BEFORE initializing the consolidated manager
    await this.registerDefaultServices();
    
    // Now initialize the consolidated system (this will initialize the services)
    await this.consolidatedManager.initialize(dispatch, options);
    
    console.log('✅ WebSocket System - Consolidated system initialized');
    this.initialized = true;
  }
  
  // Connect to WebSocket
  async connect(): Promise<void> {
    if (!this.initialized) {
      throw new Error('WebSocketSystem must be initialized before connecting');
    }
    
    await this.consolidatedManager.connect();
  }
  
  // Disconnect from WebSocket
  disconnect(): void {
    this.consolidatedManager.disconnect();
  }
  
  // Send message
  sendMessage(type: string, data?: any): void {
    this.consolidatedManager.sendMessage(type, data);
  }

  // Farm subscription management
  subscribeFarm(farmId: string): void {
    this.consolidatedManager.subscribeFarm(farmId);
  }

  unsubscribeFarm(): void {
    this.consolidatedManager.unsubscribeFarm();
  }
  
  // Get connection status
  getConnectionState(): string {
    return this.consolidatedManager.getConnectionState();
  }
  
  // Check if connected
  isConnected(): boolean {
    return this.consolidatedManager.isConnected();
  }
  
  // Register a custom service
  async registerService(service: any): Promise<void> {
    await this.consolidatedManager.registerService(service);
  }
  
  // Get statistics
  getStatistics() {
    return this.consolidatedManager.getStatistics();
  }
  
  // Get the underlying manager for advanced usage
  getManager() {
    return this.consolidatedManager;
  }
  
  // Cleanup
  async cleanup(): Promise<void> {
    if (!this.initialized) return;
    
    await this.consolidatedManager.cleanup();
    this.initialized = false;
    console.log('🧹 WebSocket System - Cleanup complete');
  }
  
  // Register default services
  private async registerDefaultServices(): Promise<void> {
    try {
      await this.consolidatedManager.registerService(deviceWebSocketService);
      await this.consolidatedManager.registerService(valveNodeWebSocketService);
      await this.consolidatedManager.registerService(alertWebSocketService);
      await this.consolidatedManager.registerService(programExecutionWebSocketService);
      await this.consolidatedManager.registerService(optaWebSocketService);
      console.log('✅ WebSocket System - Default services registered');
    } catch (error) {
      console.error('❌ WebSocket System - Failed to register default services:', error);
    }
  }
  
  // Feature flag information
  getFeatureFlags() {
    return WEBSOCKET_FEATURE_FLAGS;
  }
}

// Export main system instance
export const webSocketSystem = WebSocketSystem.getInstance();

// Export convenience functions
export const initializeWebSocketSystem = (dispatch: AppDispatch, options?: WebSocketOptions) => {
  return webSocketSystem.initialize(dispatch, options);
};

// Export individual components for advanced usage
export {
  ConsolidatedWebSocketManager,
  DeviceWebSocketService,
  ValveNodeWebSocketService,
  ProgramExecutionWebSocketService,
  deviceWebSocketService,
  valveNodeWebSocketService,
  programExecutionWebSocketService,
};

// Export feature flags
export { WEBSOCKET_FEATURE_FLAGS };